import {
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useRouter } from "next/router";
import { listingStore } from "src/stores/listing/listing.store";
import {
  LISTING_KEY_TO_QUERY_PARAM,
  useHandleSearchParams,
  useRemoveFilters,
} from "src/hooks/use-handle-search-params";
import { useFilterTracking } from "src/hooks/use-filter-tracking";
import { useListingState } from "src/hooks/stores/use-listing-store";
import type { ListingFacetKeys } from "src/types/adapters/listing.type";
import type {
  Filter as SegmentFilter,
  SEGMENT_LIST_FORMAT,
  SEGMENT_LIST_ID,
  SEGMENT_LIST_TYPE,
} from "src/segment";
import type { ContextKey } from "src/utils/product-list-context-keys";
import type { Gender, ProductListType } from "src/types/product-listing";

export type FacetContextValue = {
  refine: (key: ListingFacetKeys, value: string) => void;
  refineSingle: (key: ListingFacetKeys, value: string) => void;
  setRange: (key: ListingFacetKeys, min: number, max: number) => void;
  clearKey: (key: ListingFacetKeys) => void;
  trackFilters: (filtersApplied: SegmentFilter[]) => void;
  trackFilterReset: () => void;
  handleScrollToTop: () => void;
  pageContentType: ProductListType;
  selectedFacets: readonly ListingFacetKeys[];
  gender?: Gender;
  categoryPageId?: string;
  categorySlug?: string;
  gridRef: MutableRefObject<HTMLDivElement | null>;
};

const FacetContext = createContext<FacetContextValue | null>(null);

type FacetProviderProps = {
  children: ReactNode;
  handleScrollToTop: () => void;
  listFormat: SEGMENT_LIST_FORMAT;
  listId: SEGMENT_LIST_ID | string;
  listType: SEGMENT_LIST_TYPE | ContextKey;
  shopType?: string;
  pageContentType: ProductListType;
  selectedFacets: readonly ListingFacetKeys[];
  gender?: Gender;
  categorySlug?: string;
  gridRef: MutableRefObject<HTMLDivElement | null>;
};

export function FacetProvider({
  children,
  handleScrollToTop,
  listFormat,
  listId,
  listType,
  shopType,
  pageContentType,
  selectedFacets,
  gender,
  categorySlug,
  gridRef,
}: FacetProviderProps) {
  const router = useRouter();
  const { handleSearchParamsPrice } = useHandleSearchParams();
  const { removeAllFiltersByKey, removePriceFilters } = useRemoveFilters();
  // Constructor's result_id for the current listing lives in the listing store
  // (same source the Constructor tracking markers read); thread it into the
  // filter-tracking events so they aren't emitted with an empty result_id.
  const { resultId } = useListingState();
  const { trackFilters, trackFilterReset } = useFilterTracking({
    listFormat,
    listId,
    listType,
    shopType,
    resultId,
  });

  const refine = useCallback(
    (key: ListingFacetKeys, value: string) => {
      const { appliedFacets } = listingStore.getSnapshot();
      const currentValues = appliedFacets[key] || [];
      const isSelected = currentValues.includes(value);
      const nextValues = isSelected
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      const url = new URL(location.toString());
      const paramKey = LISTING_KEY_TO_QUERY_PARAM[key];
      url.searchParams.delete(paramKey);

      for (const value of nextValues) {
        url.searchParams.append(paramKey, value);
      }

      url.searchParams.delete("page");

      void router.push(url.toString(), undefined, {
        scroll: false,
        shallow: true,
      });

      listingStore.setFacetValues(key, nextValues);

      trackFilters([{ type: key, value }]);
      handleScrollToTop();
    },
    [router, trackFilters, handleScrollToTop]
  );

  const refineSingle = useCallback(
    (key: ListingFacetKeys, value: string) => {
      const { appliedFacets } = listingStore.getSnapshot();
      const currentValues = appliedFacets[key] || [];
      const isSelected = currentValues.includes(value);
      const nextValues = isSelected ? [] : [value];

      const url = new URL(location.toString());
      const paramKey = LISTING_KEY_TO_QUERY_PARAM[key];
      url.searchParams.delete(paramKey);

      for (const value of nextValues) {
        url.searchParams.append(paramKey, value);
      }

      url.searchParams.delete("page");

      void router.push(url.toString(), undefined, {
        scroll: false,
        shallow: true,
      });

      listingStore.setFacetValues(key, nextValues);

      trackFilters([{ type: key, value }]);
      handleScrollToTop();
    },
    [router, trackFilters, handleScrollToTop]
  );

  const setRange = useCallback(
    (key: ListingFacetKeys, min: number, max: number) => {
      const value = `${min}-${max}`;
      listingStore.setFacetValues(key, [value]);
      handleSearchParamsPrice({ min, max });
      trackFilters([{ type: key, value }]);
      handleScrollToTop();
    },
    [handleSearchParamsPrice, trackFilters, handleScrollToTop]
  );

  const clearKey = useCallback(
    (key: ListingFacetKeys) => {
      listingStore.clearFacet(key);

      if (key === "price") {
        removePriceFilters();
      } else {
        removeAllFiltersByKey(key, undefined, true);
      }

      trackFilterReset();
      handleScrollToTop();
    },
    [
      removeAllFiltersByKey,
      removePriceFilters,
      trackFilterReset,
      handleScrollToTop,
    ]
  );

  const value = useMemo(
    () => ({
      refine,
      refineSingle,
      setRange,
      clearKey,
      trackFilters,
      trackFilterReset,
      handleScrollToTop,
      selectedFacets,
      pageContentType,
      gender,
      categorySlug,
      gridRef,
    }),
    [
      refine,
      refineSingle,
      setRange,
      clearKey,
      trackFilters,
      trackFilterReset,
      handleScrollToTop,
      selectedFacets,
      pageContentType,
      gender,
      categorySlug,
      gridRef,
    ]
  );

  return (
    <FacetContext.Provider value={value}>{children}</FacetContext.Provider>
  );
}

export function useFacetContext(): FacetContextValue {
  const facetContext = useContext(FacetContext);

  if (!facetContext) {
    throw new Error("useFacetContext must be used within FacetProvider");
  }

  return facetContext;
}
